import { useCallback, useMemo, useRef } from 'react';

/** Movement (px) below which a press still counts as a click, not a drag. */
const DRAG_THRESHOLD_PX = 3;

/**
 * Mouse drag-to-scroll for a horizontal strip (the /community-library channel
 * and campaign rows). Spread `dragHandlers` on the scroll container and wrap
 * every item's click in `guardClick`, so the click that ends a drag does not
 * also select or open the item it was released on. Touch devices keep their
 * native scrolling; these are mouse events only.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);
  const hasDragged = useRef(false);

  const endDrag = useCallback(() => {
    isDragging.current = false;
    if (ref.current) {
      ref.current.style.cursor = 'grab';
      ref.current.style.removeProperty('user-select');
    }
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!ref.current) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    scrollStartX.current = ref.current.scrollLeft;
    ref.current.style.cursor = 'grabbing';
    ref.current.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !ref.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) hasDragged.current = true;
    ref.current.scrollLeft = scrollStartX.current - dx;
  }, []);

  const onMouseLeave = useCallback(() => {
    if (isDragging.current) endDrag();
  }, [endDrag]);

  const guardClick = useCallback((callback: () => void) => {
    if (hasDragged.current) return;
    callback();
  }, []);

  const dragHandlers = useMemo(() => ({
    onMouseDown,
    onMouseMove,
    onMouseUp: endDrag,
    onMouseLeave,
    style: { cursor: 'grab' } as const,
  }), [onMouseDown, onMouseMove, endDrag, onMouseLeave]);

  return { ref, dragHandlers, guardClick };
}
